import React, { useRef, useState, useEffect } from 'react';
import { log, debounce } from '../../platformui/Helpers/helpers';
import Spinner from '../../platformui/Spinner';
import ToolTipModal from '../tooltip-modal';

import * as util from './helpers';

const Report = (props) => {
  if (typeof window === 'undefined' || !window['powerbi-client'])
    return <Spinner alt={true} />;

  const report = useRef({
    layoutVisuals: null,
    token: props.token,
    layoutReport: null,
    layoutPageName: null,
    isRendered: false,
  });
  const form = useRef(null);
  const [errors, setErrors] = useState(null);
  const [check, setCheck] = useState(0);
  const [frameHeight, setFrameHeight] = useState(0);

  const ID = `${props.CurrentPageProps.ReportID}_${props.SectionID}`;
  const container = useRef(null);
  const models = window['powerbi-client'].models;

  // Embed configuration used to describe the what and how to embed.
  // This object is used when calling powerbi.embed.
  // This also includes settings and options such as filters.
  // You can find more information at https://github.com/Microsoft/PowerBI-JavaScript/wiki/Embed-Configuration-Details.
  const config = {
    type: 'report',
    tokenType: models.TokenType.Embed,
    accessToken: props.token,
    embedUrl: props.url,
    id: props.CurrentPageProps.ReportID,
    permissions: models.Permissions.Read,
    settings: {
      filterPaneEnabled: false,
      navContentPaneEnabled: false,
      layoutType: models.LayoutType.Custom,
      customLayout: {
        pageSize: {
          type: models.PageSizeType.Custom,
          width: 800,
          height: 450,
        },
        displayOption: models.DisplayOption.FitToPage,
      },
    },
  };

  // Get a reference to the embedded report HTML element
  // useRef

  const addVisualsArray = (visuals) => {
    const filteredVisuals = visuals
      .filter(util.visualsToExclude)
      .map(util.createVisualObject);
    return filteredVisuals;
  };

  // Embed the report and display it within the div container.
  const readyToEmbed = Boolean(
    props.apiLoaded === 'DONE' && container && container.current
  );

  const onRendered = () => {};

  useEffect(() => {
    if (readyToEmbed && report.current.layoutReport === null) {
      report.current.layoutReport = window['powerbi'].embed(
        container.current,
        config
      );
      setTimeout(() => {
        addEventListeners();
      }, 0);
    } else if (report.current.layoutReport === null && check < 10) {
      setCheck(check + 1);
    }
  });

  useEffect(() => {
    // On every resize (max once per 100ms) we rerender the visuals.
    const onResize = debounce(() => {
      renderVisuals();
    }, 100);
    // Bind the resize handler.
    window.addEventListener('resize', onResize);
    // Return a cleanup function that run on unmount.
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const addEventListeners = () => {
    // Report.off removes a given event handler if it exists.
    report.current.layoutReport.off('loaded');

    // Report.on will add an event handler which prints to Log window.
    report.current.layoutReport.on('loaded', async () => {
      await report.current.layoutReport.setPage(props.SectionID);
      report.current.layoutReport.getPages().then((pages) => {
        const activePage = pages.find((page) => page.isActive);
        report.current.layoutPageName = activePage.name;
        activePage.getVisuals().then((visuals) => {
          report.current.layoutVisuals = addVisualsArray(visuals);
          renderVisuals();
        });
      });
    });

    report.current.layoutReport.off('rendered');
    report.current.layoutReport.on('rendered', onRendered);

    report.current.layoutReport.off('error');
    report.current.layoutReport.on('error', function () {
      log()('error');
      setErrors('Error in report!');
    });
  };

  const renderVisuals = () => {
    const LayoutShowcaseConsts = {
      margin: 10,
      minPageWidth: 270,
    };

    // render only if report and visuals initialized
    if (!report.current.layoutReport || !report.current.layoutVisuals) return;

    // Get models. models contains enums that can be used
    const models = window['powerbi-client'].models;

    // Get embedContainer width and height
    let pageWidth = container.current.clientWidth; // $('#embedContainer').width();
    let pageHeight = container.current.clientHeight;

    // Calculating the overall width of the visuals in each row
    let visualsTotalWidth = pageWidth - LayoutShowcaseConsts.margin * 2;

    // Calculate the width of a single visual, according to the number of columns
    // For one and three columns visuals width will be a third of visuals total width
    let width = visualsTotalWidth;

    // Set visuals height according to width - 9:16 ratio
    const height = width * (9 / 16);

    // Visuals starting point
    let x = LayoutShowcaseConsts.margin,
      y = LayoutShowcaseConsts.margin;

    // Filter the visuals list to display only the checked visuals
    let checkedVisuals = report.current.layoutVisuals;

    // Calculate the number of lines
    const lines = report.current.layoutVisuals.length;

    // Calculate page height with margins
    pageHeight = Math.max(
      pageHeight,
      lines * height + (lines + 1) * LayoutShowcaseConsts.margin
    );

    // Building visualsLayout object
    // You can find more information at https://github.com/Microsoft/PowerBI-JavaScript/wiki/Custom-Layout
    let visualsLayout = {};
    for (let i = 0; i < checkedVisuals.length; i++) {
      visualsLayout[checkedVisuals[i].name] = {
        x: x,
        y: y,
        width: width,
        height: height,
        displayState: {
          // Change the selected visuals display mode to visible
          mode: models.VisualContainerDisplayMode.Visible,
        },
      };

      // Calculating (x,y) position for the next visual
      x += width + LayoutShowcaseConsts.margin;
      if (x + width > pageWidth) {
        x = LayoutShowcaseConsts.margin;
        y += height + LayoutShowcaseConsts.margin;
      }
    }

    // Building pagesLayout object
    let pagesLayout = {};
    pagesLayout[report.current.layoutPageName] = {
      defaultLayout: {
        displayState: {
          // Default display mode for visuals is hidden
          mode: models.VisualContainerDisplayMode.Hidden,
        },
      },
      visualsLayout: visualsLayout,
    };

    // Building settings object
    let settings = {
      layoutType: models.LayoutType.Custom,
      customLayout: {
        pageSize: {
          type: models.PageSizeType.Custom,
          width: pageWidth - 10,
          height: pageHeight - 20,
        },
        displayOption: models.DisplayOption.FitToPage,
        pagesLayout: pagesLayout,
      },
    };

    // Change page background to transparent on Two / Three columns configuration
    settings.background = models.BackgroundType.Transparent;

    // Call updateSettings with the new settings object
    report.current.layoutReport.updateSettings(settings);

    setFrameHeight(Math.ceil(pageHeight));
  };

  // Information about setAccessToken is found here: https://docs.microsoft.com/en-us/javascript/api/overview/powerbi/refresh-token .
  // We might consider using more parts of the code sample in the future.
  useEffect(() => {
    if (report.current.token !== props.token) {
      const report = window['powerbi'].get(container.current);
      report.setAccessToken(props.token);
      renderVisuals();
    }
  }, [props.token]);

  useEffect(() => {
    if (report.current.layoutVisuals !== null) {
      const filters = util.createFilters(props.filters);
      report.current.layoutReport
        .setFilters(filters)
        .catch((error) => log()(error));
    }
  }, [props.filters]);

  const doExport =
    (fileExtension = 'csv') =>
    (e) => {
      e.preventDefault();
      const data = {
        filters: util.formatFiltersForExport(props.filters),
        name: form.current.querySelector('[name=visual]').value,
      };
      util.exportVisualData(data, report.current.layoutReport, fileExtension);
    };

  const getExportOptions = () => {
    if (props.CurrentPageProps.DisableExport) {
      return null;
    }
    return (
      <ToolTipModal
        id={`export_tooltip_${props.CurrentPageProps.ReportID}`}
        header={props.CurrentPageProps.Translations.exportheadline}
        closeText={props.CurrentPageProps.Translations.closeexport}
        openText={props.CurrentPageProps.Translations.export}
      >
        <form ref={form} onSubmit={doExport()} method="post">
          <div className="c-forms__select">
            <select name="visual">
              {report.current.layoutVisuals &&
                report.current.layoutVisuals.map((o) => (
                  <option value={o.name} key={o.name}>
                    {o.title}
                  </option>
                ))}
            </select>
          </div>
          <button
            onClick={doExport('csv')}
            name="format"
            value="csv"
            className=""
            type="submit"
          >
            CSV
          </button>
          <button
            onClick={doExport('xlsx')}
            value="format"
            className=""
            type="submit"
          >
            Excel
          </button>
        </form>
      </ToolTipModal>
    );
  };

  return (
    <div className="c-report" id={props.AnchorSectionId}>
      {errors && <p>Error: {errors.detailedMessage || errors.message}</p>}
      {!report.current.layoutVisuals && <Spinner alt={true} />}
      {props.Heading && <h2>{props.Heading}</h2>}
      {props.Preamble && <p>{props.Preamble}</p>}
      {getExportOptions()}
      <div
        className="powerbi-iframe-container"
        style={{ height: `${frameHeight + 100}px`, width: '100%' }}
        ref={container}
        id={ID}
      ></div>
    </div>
  );
};

export default Report;
